import { TypedDocumentNode, gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import {
  CardRarity,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { blockchainRarities } from '@sorare/core/src/lib/cards';

import {
  BaseballRecentCurrentUserCardsQuery,
  BaseballRecentCurrentUserCardsQueryVariables,
  FootballRecentCurrentUserCardsQuery,
  FootballRecentCurrentUserCardsQueryVariables,
  NBARecentCurrentUserCardsQuery,
  NBARecentCurrentUserCardsQueryVariables,
} from './__generated__/useCurrentUserRecentCards.graphql';

const FOOTBALL_RECENT_CURRENT_USER_CARDS_QUERY = gql`
  query FootballRecentCurrentUserCardsQuery(
    $first: Int!
    $rarities: [Rarity!]
  ) {
    currentUser {
      slug
      footballCards(first: $first, rarities: $rarities) {
        nodes {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  FootballRecentCurrentUserCardsQuery,
  FootballRecentCurrentUserCardsQueryVariables
>;

const BASEBALL_RECENT_CURRENT_USER_CARDS_QUERY = gql`
  query BaseballRecentCurrentUserCardsQuery(
    $first: Int!
    $rarities: [CardRarity!]
  ) {
    currentUser {
      slug
      baseballCards(first: $first, rarities: $rarities) {
        nodes {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  BaseballRecentCurrentUserCardsQuery,
  BaseballRecentCurrentUserCardsQueryVariables
>;

const NBA_RECENT_CURRENT_USER_CARDS_QUERY = gql`
  query NBARecentCurrentUserCardsQuery($first: Int!, $rarities: [CardRarity!]) {
    currentUser {
      slug
      nbaCards(first: $first, rarities: $rarities) {
        nodes {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  NBARecentCurrentUserCardsQuery,
  NBARecentCurrentUserCardsQueryVariables
>;

const FIRST_LIMIT = 8;

const usSportBlockchainRarities = blockchainRarities.filter(
  r => r !== 'custom_series'
) as CardRarity[];

export const useCurrentUserRecentCards = (
  sport: Sport,
  {
    skip,
  }: {
    skip?: boolean;
  }
) => {
  const { includeCommonCards } = useSearchCardsContext();

  const { data: dataBaseball } = useQuery(
    BASEBALL_RECENT_CURRENT_USER_CARDS_QUERY,
    {
      variables: {
        first: FIRST_LIMIT,
        rarities: includeCommonCards ? null : usSportBlockchainRarities,
      },
      skip: skip || sport !== Sport.BASEBALL,
      fetchPolicy: 'network-only',
    }
  );

  const { data: dataNBA } = useQuery(NBA_RECENT_CURRENT_USER_CARDS_QUERY, {
    variables: {
      first: FIRST_LIMIT,
      rarities: includeCommonCards ? null : usSportBlockchainRarities,
    },
    skip: skip || sport !== Sport.NBA,
    fetchPolicy: 'network-only',
  });

  const { data: dataFootball } = useQuery(
    FOOTBALL_RECENT_CURRENT_USER_CARDS_QUERY,
    {
      variables: {
        first: FIRST_LIMIT,
        rarities: includeCommonCards
          ? null
          : (blockchainRarities as unknown as Rarity[]),
      },
      skip: skip || sport !== Sport.FOOTBALL,
      fetchPolicy: 'network-only',
    }
  );

  return useMemo(
    () =>
      dataBaseball?.currentUser?.baseballCards.nodes ||
      dataNBA?.currentUser?.nbaCards.nodes ||
      dataFootball?.currentUser?.footballCards.nodes ||
      [],
    [
      dataBaseball?.currentUser?.baseballCards.nodes,
      dataFootball?.currentUser?.footballCards.nodes,
      dataNBA?.currentUser?.nbaCards.nodes,
    ]
  );
};
